import {
  Box,
  ButtonGroup,
  Flex,
  FormControl,
  SimpleGrid,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Card from "../../../components/card/Card";
import CustomButton from "../../../components/Common/CustomButton";
import IsLoading from "../../../components/Common/IsLoading";
import {
  selectButtonLoading,
  selectFormData,
  selectHoldItems,
  selectLoading,
  selectShippingForm,
} from "./redux/selector";
import { actions } from "./redux/slice";
import ModalTemplate from "../../../components/Common/ModalTemplate";
import { OrderStatusType, PaymentMethodType } from "./types";
import OrderDetailForm from "./OrderDetailForm";
import CustomInput from "../../../components/Common/CustomInput";
import ReactDatePicker2 from "../../../components/Common/CustomDatePicker/ReactDatePicker2";
import { IS_RETAILER } from "../../../utils/Api";

const FieldRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <Flex>
    <Text fontWeight="semibold" minW="180px">
      {label}:
    </Text>
    <Text>{value || "-"}</Text>
  </Flex>
);

const OrderDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const form = useSelector(selectFormData);
  const loader = useSelector(selectLoading);
  const loading = useSelector(selectButtonLoading);
  const [showModal, setShowModal] = React.useState(false);
  const textColor = useColorModeValue("navy.700", "white");
  const holdItems = useSelector(selectHoldItems);
  const shippingForm = useSelector(selectShippingForm);
  const [firstOrderAction, setFirstOrderAction] = useState<OrderStatusType>(
    OrderStatusType.NULL
  );

  useEffect(() => {
    if (params.id) dispatch(actions.doGetById(params.id));
    return () => {};
  }, [params.id]);

  const handleCancel = () => {
    dispatch(actions.clearFrom());
    navigate(-1);
  };

  const handleWaitingAdminUpdate = () => {
    dispatch(
      actions.doUpdateStatus({
        action: returnOrderStatus(),
        id: params.id as string,
        callback() {
          setShowModal(false);
          setFirstOrderAction(OrderStatusType.NULL);
          dispatch(actions.doGetById(params.id as string));
        },
      })
    );
  };

  const returnOrderStatus = () => {
    if (firstOrderAction === OrderStatusType.PROCESSING) {
      return OrderStatusType.SHIPPED;
    }
    if (firstOrderAction === OrderStatusType.SHIPPED) {
      return OrderStatusType.DELIVERED;
    }
    return firstOrderAction;
  };

  const renderModalTitle = () => {
    if (firstOrderAction === OrderStatusType.ACCEPTED) {
      return "order.modal.acceptOrder";
    } else if (firstOrderAction === OrderStatusType.REJECTED) {
      return "order.modal.rejectOrder";
    } else if (firstOrderAction === OrderStatusType.HOLD) {
      return "order.modal.holdOrder";
    } else if (firstOrderAction === OrderStatusType.PROCESSING) {
      return "order.modal.shipOrder";
    } else if (firstOrderAction === OrderStatusType.SHIPPED) {
      return "order.modal.deliverOrder";
    } else if (firstOrderAction === OrderStatusType.READY_FOR_STORE_PICKUP) {
      return "order.modal.readyForStorePickup";
    }
    return "order.modal.updateOrderStatus";
  };
  const renderModalDescription = () => {
    if (firstOrderAction === OrderStatusType.ACCEPTED) {
      return "order.modal.acceptOrderDescription";
    } else if (firstOrderAction === OrderStatusType.REJECTED) {
      return "order.modal.rejectOrderDescription";
    } else if (firstOrderAction === OrderStatusType.HOLD) {
      return "order.modal.holdOrderDescription";
    } else if (firstOrderAction === OrderStatusType.PROCESSING) {
      return "order.modal.shipOrderDescription";
    } else if (firstOrderAction === OrderStatusType.SHIPPED) {
      return "order.modal.deliverOrderDescription";
    } else if (firstOrderAction === OrderStatusType.READY_FOR_STORE_PICKUP) {
      return "order.modal.readyForStorePickupDescription";
    }
    return "order.modal.updateOrderStatusDescription";
  };

  const renderModalActionButton = () => {
    if (firstOrderAction === OrderStatusType.ACCEPTED) {
      return "order.modal.accept";
    } else if (firstOrderAction === OrderStatusType.REJECTED) {
      return "order.modal.reject";
    } else if (firstOrderAction === OrderStatusType.HOLD) {
      return "order.modal.hold";
    } else if (firstOrderAction === OrderStatusType.PROCESSING) {
      return "order.modal.ship";
    } else if (firstOrderAction === OrderStatusType.SHIPPED) {
      return "order.modal.deliver";
    } else if (firstOrderAction === OrderStatusType.READY_FOR_STORE_PICKUP) {
      return "order.modal.readyForStorePickup";
    }
    return "order.modal.update";
  };

  const handleFieldChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    dispatch(actions.updateShippingFormValue({ key: name, value: value }));
  };
  const handleDateFieldChange = (key: string, value: any) => {
    dispatch(actions.updateShippingFormValue({ key: key, value: value }));
  };

  const checkDeliveryButtonRender = () => {
    return (
      form.status === OrderStatusType.SHIPPED ||
      form.status === OrderStatusType.READY_FOR_STORE_PICKUP
    );
  };
  const checkFirstButtonRender = () => {
    return (
      form.status === OrderStatusType.WAITING_FOR_ADMIN ||
      form.status === OrderStatusType.QUEUED_FOR_UN_HOLD
    );
  };

  return (
    <Flex pt={{ base: "130px", md: "80px", xl: "80px" }} position="relative">
      <Card>
        {loader ? (
          <IsLoading />
        ) : (
          <FormControl>
            <SimpleGrid
              columns={1}
              spacing={10}
              position="sticky"
              top={20}
              zIndex={999}
            >
              <Flex flexDirection="column">
                <ButtonGroup justifyContent="flex-end" mt={8}>
                  <CustomButton
                    handleClick={handleCancel}
                    title={t("common.button.back")}
                    loading={false}
                    variant="light"
                    sx={{ marginRight: "20px" }}
                  />
                  {checkFirstButtonRender() && (
                    <CustomButton
                      handleClick={() => {
                        setShowModal(true);
                        setFirstOrderAction(OrderStatusType.ACCEPTED);
                      }}
                      title={t("order.button.accept")}
                      loading={false}
                      variant="brand"
                      sx={{ marginRight: "20px" }}
                    />
                  )}
                  {checkFirstButtonRender() && (
                    <CustomButton
                      handleClick={() => {
                        setShowModal(true);
                        setFirstOrderAction(OrderStatusType.REJECTED);
                      }}
                      title={t("order.button.reject")}
                      loading={false}
                      variant="brand"
                      sx={{
                        marginRight: "20px",
                        background: "red !important",
                        _hover: {
                          background: "red",
                        },
                      }}
                    />
                  )}
                  {IS_RETAILER !== "true" && form.status === OrderStatusType.WAITING_FOR_ADMIN && (
                    <CustomButton
                      handleClick={() => {
                        setShowModal(true);
                        setFirstOrderAction(OrderStatusType.HOLD);
                      }}
                      title={t("order.button.hold")}
                      loading={false}
                      variant="brand"
                      sx={{
                        marginRight: "20px",
                        background: "#9a7d0a  !important",
                        _hover: {
                          background: "#9a7d0a ",
                        },
                      }}
                      disabled={
                        holdItems.length === 0 || form.order_items.length === 0
                      }
                    />
                  )}
                  {form.status === OrderStatusType.PROCESSING &&
                    form.paymentMethod !== PaymentMethodType.STORE_PAYMENT && (
                      <CustomButton
                        handleClick={() => {
                          setShowModal(true);
                          setFirstOrderAction(OrderStatusType.PROCESSING);
                        }}
                        title={t("order.button.ship")}
                        loading={false}
                        variant="brand"
                        sx={{ marginRight: "20px" }}
                      />
                    )}
                  {form.status === OrderStatusType.PROCESSING &&
                    form.paymentMethod === PaymentMethodType.STORE_PAYMENT && (
                      <CustomButton
                        handleClick={() => {
                          setShowModal(true);
                          setFirstOrderAction(
                            OrderStatusType.READY_FOR_STORE_PICKUP
                          );
                        }}
                        title={t("order.button.readyForStorePickup")}
                        loading={false}
                        variant="brand"
                        sx={{ marginRight: "20px" }}
                      />
                    )}
                  {checkDeliveryButtonRender() && (
                      <CustomButton
                        handleClick={() => {
                          setShowModal(true);
                          setFirstOrderAction(OrderStatusType.SHIPPED);
                        }}
                        title={t("order.button.deliver")}
                        loading={false}
                        variant="brand"
                        sx={{ marginRight: "20px" }}
                      />
                    )}
                </ButtonGroup>
              </Flex>
              </SimpleGrid>
              
            <OrderDetailForm form={form} />
          </FormControl>
        )}
      </Card>
      {showModal && (
        <ModalTemplate
          title={`${t(renderModalTitle())}`}
          open={showModal}
          handleClose={() => setShowModal(false)}
        >
          <Box>
            <Text fontSize={"x-large"} color={textColor}>
              {`${t(renderModalDescription())}`}
            </Text>

            {firstOrderAction === OrderStatusType.PROCESSING &&
              form.paymentMethod !== PaymentMethodType.STORE_PAYMENT && (
                <CustomInput
                  name={"deliveryCompany"}
                  value={shippingForm?.deliveryCompany}
                  handleChange={handleFieldChange}
                  placeholder={`${t("order.shippingForm.deliveryCompany")}`}
                  title={`${t("order.shippingForm.deliveryCompany")}`}
                />
              )}

            {firstOrderAction === OrderStatusType.PROCESSING &&
              form.paymentMethod !== PaymentMethodType.STORE_PAYMENT && (
                <CustomInput
                  name={"trackingNo"}
                  value={shippingForm?.trackingNo}
                  handleChange={handleFieldChange}
                  placeholder={`${t("order.shippingForm.trackingNo")}`}
                  title={`${t("order.shippingForm.trackingNo")}`}
                />
              )}

            {firstOrderAction === OrderStatusType.PROCESSING &&
              form.paymentMethod !== PaymentMethodType.STORE_PAYMENT && (
                <CustomInput
                  name={"trackingUrl"}
                  value={shippingForm?.trackingUrl}
                  handleChange={handleFieldChange}
                  placeholder={`${t("order.shippingForm.trackingUrl")}`}
                  title={`${t("order.shippingForm.trackingUrl")}`}
                />
              )}
            {firstOrderAction === OrderStatusType.PROCESSING &&
              form.paymentMethod !== PaymentMethodType.STORE_PAYMENT && (
                <ReactDatePicker2
                  name="estimatedDeliveryDate"
                  value={shippingForm?.estimatedDeliveryDate}
                  onChange={(val) =>
                    handleDateFieldChange("estimatedDeliveryDate", val)
                  }
                  placeholderText={t(
                    "order.shippingForm.estimatedDeliveryDate"
                  )}
                  minDate={new Date()}
                />
              )}

            <SimpleGrid columns={1} spacing={10}>
              <Flex flexDirection="column">
                <ButtonGroup justifyContent="space-between" mt={8}>
                  <CustomButton
                    handleClick={() => setShowModal(false)}
                    title={t("order.button.cancel")}
                    loading={loading}
                    sx={{
                      marginLeft: "20px",
                      background: "red !important",
                      _hover: {
                        background: "red",
                      },
                    }}
                  />
                  <CustomButton
                    handleClick={() => handleWaitingAdminUpdate()}
                    title={t(renderModalActionButton())}
                    loading={loading}
                    variant="brand"
                  />
                </ButtonGroup>
              </Flex>
            </SimpleGrid>
          </Box>
        </ModalTemplate>
      )}
    </Flex>
  );
};

export default OrderDetails;
